import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import BookingSearch from '@/features/booking/BookingSearch';
import CouponBanner from '@/features/catalog/CouponBanner';
import FeaturedTripsSection from '@/features/catalog/FeaturedTripsSection';
import FrequentTripsSection from '@/features/catalog/FrequentTripsSection';
import ServicesSection from '@/features/catalog/ServicesSection';
import ReviewsSection from '@/features/reviews/ReviewsSection';
import { useLocale } from '@/i18n/LocaleContext';
import { alternatesFor } from '@/i18n/locales';
import { useLandingSectionsQuery } from '@/services/api/contentApi';
import { useSeo } from '@/seo/seo';

export function HomePage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data: sections } = useLandingSectionsQuery(locale);

  /**
   * Whether the administrator has this block switched on.
   *
   * Defaults to visible while the configuration is still loading: the hero and
   * the search are the point of the page, and hiding them until an API call
   * returns would leave a blank screen on first paint.
   */
  const shows = (key: string) =>
    sections === undefined || sections.some((section) => section.key === key);

  const heroCopy = sections?.find((section) => section.key === 'hero');

  useSeo({
    title: `${t('site.name')} — ${t('site.tagline')}`,
    description: t('home.hero.subtitle'),
    canonical: alternatesFor('home')[locale],
    alternates: alternatesFor('home'),
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'TravelAgency',
        name: t('site.name'),
        description: t('site.tagline'),
        areaServed: { '@type': 'Country', name: 'Costa Rica' },
      },
    ],
  });

  return (
    <>
      {shows('hero') && (
        <Box
          component="section"
          sx={{
            // Painted from the prerendered HTML, so the hero is visible before any
            // JavaScript or API response arrives. Reads from the theme rather than a
            // literal hex pair so it keeps matching a color an administrator changes.
            background: (theme) =>
              `linear-gradient(160deg, ${theme.palette.primary.main} 0%, ${theme.custom.hero.gradientEnd} 100%)`,
            color: 'common.white',
            py: { xs: 8, md: 12 },
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3} sx={{ maxWidth: 720 }}>
              {/* Administrator copy wins when set; the bundled translation is the
                  fallback so the page is never empty. `color: inherit` overrides the
                  theme's own heading color (meant for headings on the page's normal
                  background) so these pick up the white set on the hero above instead. */}
              <Typography variant="h1" sx={{ color: 'inherit' }}>
                {heroCopy?.title ?? t('home.hero.title')}
              </Typography>
              <Typography variant="h3" component="p" sx={{ fontWeight: 400, opacity: 0.92, color: 'inherit' }}>
                {heroCopy?.body ?? t('home.hero.subtitle')}
              </Typography>
            </Stack>

            {shows('search') && (
              <Box sx={{ mt: 5 }}>
                <BookingSearch />
              </Box>
            )}
          </Container>
        </Box>
      )}

      <CouponBanner />
      <FrequentTripsSection />
      <ServicesSection />
      {shows('reviews') && <ReviewsSection />}
      <FeaturedTripsSection />
    </>
  );
}

export default HomePage;
